import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'hooks';
import { Box, Typography, Button, Link } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import Page from 'components/Page';
import PageFooter from 'components/PageFooter';
import { setSelectedBFMC, setSelectedPlanType } from 'state/guide';
import { BFMCManager } from 'models';

interface PageProps {}

const LandingPage: React.FunctionComponent<PageProps> = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const selectMainRoute = (planType: BFMCManager.PlanType) => {
        dispatch(setSelectedPlanType(planType));
        if (planType === 'faft') {
            dispatch(setSelectedBFMC(null));
        }
        navigate('/plan');
    };

    return (
        <Page>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100%',
                    position: 'relative',
                    backgroundColor: '#2B1520',
                    overflow: 'hidden',
                    mx: -1,
                    my: -1,
                }}
            >
                {/* Background contour overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'url(/hero-contours.png)',
                        backgroundPosition: 'right top',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: { xs: '80%', sm: '70%', md: '60%' },
                        opacity: 0.08,
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        zIndex: 2,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 1000,
                            mx: 'auto',
                            px: { xs: 2, sm: 3, md: 4 },
                            pt: { xs: 4, sm: 5, md: 6 },
                            pb: { xs: 3, sm: 4, md: 5 },
                            position: 'relative',
                            zIndex: 10,
                            width: '100%',
                        }}
                    >
                        <Typography
                            variant="h2"
                            component="h1"
                            align="center"
                            sx={{
                                mb: { xs: 3, sm: 4, md: 5 },
                                fontSize: {
                                    xs: '1.75rem',
                                    sm: '2.25rem',
                                    md: '3rem',
                                },
                                color: '#FFFFFF',
                                fontWeight: 700,
                            }}
                        >
                            Welcome to the Bushfire Information Portal (BIP)
                            Pilot
                        </Typography>

                        <Box
                            sx={{
                                textAlign: 'center',
                                mb: { xs: 3, sm: 4, md: 5 },
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.95)',
                                    fontSize: {
                                        xs: '0.95rem',
                                        sm: '1rem',
                                        md: '1.1rem',
                                    },
                                    lineHeight: 1.6,
                                }}
                            >
                                For more information about the BIP pilot
                                program, visit the{' '}
                                <Link
                                    href="https://sbcc.sa.gov.au/bushfire-management-area-plans/bmap-uplift-project-release-details-and-resources/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        color: '#FFFFFF',
                                        fontWeight: 700,
                                        textDecoration: 'underline',
                                        textDecorationColor:
                                            'rgba(255, 255, 255, 0.5)',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            color: 'rgb(93, 31, 52)',
                                            backgroundColor: '#FFFFFF',
                                            textDecoration: 'underline',
                                            textDecorationColor:
                                                'rgb(93, 31, 52)',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 0.5,
                                        },
                                    }}
                                >
                                    State Bushfire Coordination Committee
                                    website
                                </Link>
                                .
                            </Typography>
                        </Box>

                        {/* Disabled "Endorsed BMAPs" placeholder */}
                        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                            <Box
                                role="button"
                                aria-disabled="true"
                                sx={{
                                    width: '100%',
                                    minHeight: { xs: 80, sm: 90, md: 100 },
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                    backgroundColor: '#f5f5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: { xs: 3, sm: 4, md: 5 },
                                    py: { xs: 2.5, sm: 3, md: 3.5 },
                                    borderRadius: 1,
                                    opacity: 0.6,
                                    cursor: 'not-allowed',
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontSize: {
                                            xs: '1.25rem',
                                            sm: '1.5rem',
                                            md: '1.75rem',
                                        },
                                        color: '#999',
                                        fontWeight: 700,
                                    }}
                                >
                                    Endorsed Bushfire Management Area Plans
                                    (BMAPs)
                                </Typography>
                                <ArrowForwardIcon
                                    sx={{
                                        color: '#ccc',
                                        fontSize: { xs: 28, sm: 32, md: 36 },
                                        ml: 2,
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Three sub-cards */}
                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Button
                                    onClick={() => selectMainRoute('bfrmp')}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        minHeight: {
                                            xs: 180,
                                            sm: 200,
                                            md: 220,
                                        },
                                        backgroundColor: 'white',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        textAlign: 'left',
                                        p: { xs: 3, sm: 3.5, md: 4 },
                                        textTransform: 'none',
                                        borderRadius: 1,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            boxShadow:
                                                '0 6px 16px rgba(0,0,0,0.25)',
                                            transform: 'translateY(-2px)',
                                            backgroundColor: 'white',
                                            '& .arrow-icon': {
                                                transform: 'translateX(4px)',
                                            },
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontSize: {
                                                xs: '1.15rem',
                                                sm: '1.25rem',
                                                md: '1.35rem',
                                            },
                                            color: '#333',
                                            fontWeight: 700,
                                            mb: 'auto',
                                        }}
                                    >
                                        BMAP Development – System Pilot
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            mt: 2,
                                        }}
                                    >
                                        <ArrowForwardIcon
                                            className="arrow-icon"
                                            sx={{
                                                color: '#999',
                                                fontSize: { xs: 24, sm: 28 },
                                                transition:
                                                    'transform 0.3s ease',
                                            }}
                                        />
                                    </Box>
                                </Button>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <Box
                                    role="button"
                                    aria-disabled="true"
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        minHeight: {
                                            xs: 180,
                                            sm: 200,
                                            md: 220,
                                        },
                                        backgroundColor: '#f5f5f5',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        textAlign: 'left',
                                        p: { xs: 3, sm: 3.5, md: 4 },
                                        borderRadius: 1,
                                        opacity: 0.6,
                                        cursor: 'not-allowed',
                                    }}
                                >
                                    <Box sx={{ mb: 2 }}>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontSize: {
                                                    xs: '1.15rem',
                                                    sm: '1.25rem',
                                                    md: '1.35rem',
                                                },
                                                color: '#999',
                                                fontWeight: 700,
                                                mb: 1.5,
                                            }}
                                        >
                                            Implementation planning
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#aaa',
                                                fontSize: {
                                                    xs: '0.875rem',
                                                    sm: '0.9rem',
                                                },
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            BMAP implementation through
                                            coordinated seasonal planning and
                                            assigning of responsibilities.
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        <ArrowForwardIcon
                                            sx={{
                                                color: '#ccc',
                                                fontSize: { xs: 24, sm: 28 },
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <Box
                                    role="button"
                                    aria-disabled="true"
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        minHeight: {
                                            xs: 180,
                                            sm: 200,
                                            md: 220,
                                        },
                                        backgroundColor: '#f5f5f5',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        textAlign: 'left',
                                        p: { xs: 3, sm: 3.5, md: 4 },
                                        borderRadius: 1,
                                        opacity: 0.6,
                                        cursor: 'not-allowed',
                                    }}
                                >
                                    <Box sx={{ mb: 2 }}>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontSize: {
                                                    xs: '1.15rem',
                                                    sm: '1.25rem',
                                                    md: '1.35rem',
                                                },
                                                color: '#999',
                                                fontWeight: 700,
                                                mb: 1.5,
                                            }}
                                        >
                                            Implementation status and review
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#aaa',
                                                fontSize: {
                                                    xs: '0.875rem',
                                                    sm: '0.9rem',
                                                },
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            Assess effectiveness of
                                            implementation and status of
                                            treatments
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        <ArrowForwardIcon
                                            sx={{
                                                color: '#ccc',
                                                fontSize: { xs: 24, sm: 28 },
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>

                <PageFooter />
            </Box>
        </Page>
    );
};

export default LandingPage;
